import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Shield, Compass, Users, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import SpotlightBackground from "@/components/SpotlightBackground";
import DestinationCard from "@/components/DestinationCard";

const CATS = [
  { slug: "international", label: "International" },
  { slug: "domestic", label: "Domestic" },
  { slug: "fixed-departures", label: "Fixed Departures" },
  { slug: "short-trip", label: "Short Trip" },
  { slug: "adventure", label: "Adventure" },
  { slug: "devotional", label: "Devotional" },
];

function SlotCounter({ target, suffix = "+", label, duration = 2400 }) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let frame;
    let start;
    const tick = (now) => {
      if (!start) start = now;
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setValue(Math.floor(target * eased));
      if (progress < 1) frame = requestAnimationFrame(tick);
      else setValue(target);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration]);
  const digits = String(value).split("");
  return (
    <div className="flex items-center gap-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-background/50 text-accent">
        {label === "Travellers Hosted" ? <Users className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
      </span>
      <div>
        <div className="flex items-center text-2xl sm:text-3xl font-semibold tracking-tight tabular-nums" aria-label={`${target}${suffix}`}>
          <span className="inline-flex overflow-hidden leading-none" aria-hidden="true">
            {digits.map((digit, index) => (
              <span key={`${index}-${digits.length}`} className="relative inline-block h-[1em] w-[0.62em] overflow-hidden">
                <span className="absolute left-0 top-0 flex flex-col transition-transform duration-100 ease-out" style={{ transform: `translateY(-${Number(digit) * 10}%)` }}>
                  {Array.from({ length: 10 }, (_, n) => <span key={n} className="flex h-[1em] items-center justify-center">{n}</span>)}
                </span>
              </span>
            ))}
          </span>
          <span className="text-accent">{suffix}</span>
        </div>
        <div className="mt-1 text-[10px] sm:text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [favIds, setFavIds] = useState(new Set());
  const { user } = useAuth();
  const [inq, setInq] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  useEffect(() => {
    api.get("/destinations", { params: { featured: true } }).then(({ data }) => setFeatured(data));
    if (user) api.get("/favorites").then(({ data }) => setFavIds(new Set(data.map((d) => d.id)))).catch(() => {});
  }, [user]);
  const toggleFav = async (d) => {
    if (favIds.has(d.id)) {
      await api.delete(`/favorites/${d.id}`);
      const next = new Set(favIds); next.delete(d.id); setFavIds(next);
      toast.success("Removed from favorites");
    } else {
      await api.post(`/favorites/${d.id}`);
      setFavIds(new Set(favIds).add(d.id));
      toast.success("Added to favorites");
    }
  };
  const submitInquiry = async (e) => {
    e.preventDefault(); setSending(true);
    try {
      await api.post("/inquiries", inq);
      toast.success("Inquiry sent — we'll be in touch shortly!");
      setInq({ name: "", email: "", phone: "", message: "" });
    } catch { toast.error("Failed to send inquiry"); }
    finally { setSending(false); }
  };
  return (
    <div>
      <SpotlightBackground className="min-h-[92vh] flex items-center">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-10 items-center w-full">
          <div className="lg:col-span-12 fade-up">
            <div className="flex flex-wrap items-center gap-x-10 gap-y-5 mb-9">
              <SlotCounter target={8000} label="Travellers Hosted" />
              <SlotCounter target={25} label="Years of Experience" suffix="+" duration={1900} />
            </div>
            <h1 className="heading-serif text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.05] max-w-5xl">Pure Veg <em className="text-accent not-italic">Group Tour Operator</em></h1>
            <p className="mt-5 text-sm sm:text-base font-medium tracking-wide text-accent">Over 25 years of experience <span className="mx-2 text-muted-foreground">|</span> 10,000+ customers served</p>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">Discover India and the world with thoughtfully planned pure vegetarian group holidays, memorable experiences, and the comfort of food you trust.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90" data-testid="hero-explore-btn"><Link to="/category/international">Explore Our Tours <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
              <Button asChild size="lg" variant="outline" className="rounded-full" data-testid="hero-inquiry-btn"><a href="#inquiry">Plan Your Journey</a></Button>
            </div>
            <div className="mt-12 flex items-center gap-8 text-xs text-muted-foreground">
              <div className="flex items-center gap-2"><Shield className="h-4 w-4 text-accent" /> Secure payments</div>
              <div className="flex items-center gap-2"><Compass className="h-4 w-4 text-accent" /> Live weather on every trip</div>
              <div className="hidden sm:flex items-center gap-2"><Sparkles className="h-4 w-4 text-accent" /> 5-star curation</div>
            </div>
          </div>
        </div>
      </SpotlightBackground>
      <section className="max-w-7xl mx-auto px-6 pt-10"><div className="flex flex-wrap gap-3">{CATS.map((c) => <Link key={c.slug} to={`/category/${c.slug}`} data-testid={`cat-chip-${c.slug}`} className="rounded-full border border-border px-4 py-2 text-sm hover:bg-secondary transition">{c.label}</Link>)}</div></section>
      <section className="max-w-7xl mx-auto px-6 pt-14">
        <div className="flex items-end justify-between mb-8"><div><div className="text-xs uppercase tracking-widest text-accent mb-2">Featured</div><h2 className="heading-serif text-4xl sm:text-5xl">Journeys we're loving now</h2></div></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="featured-grid">{featured.map((d, i) => <DestinationCard key={d.id} d={d} isFav={favIds.has(d.id)} onToggleFav={toggleFav} className={i === 0 ? "md:col-span-2 lg:col-span-2 lg:row-span-2" : ""} />)}</div>
      </section>
      <section id="inquiry" className="max-w-7xl mx-auto px-6 pt-24"><div className="grid lg:grid-cols-2 gap-12 items-start">
        <div><div className="text-xs uppercase tracking-widest text-accent mb-2">Plan your journey</div><h2 className="heading-serif text-4xl sm:text-5xl mb-4">Tell us where your heart wanders.</h2><p className="text-muted-foreground max-w-md">A trip designer from OITS will reach out within a working day with a bespoke, honest itinerary — no pressure, no packages.</p><div className="mt-8 grid grid-cols-2 gap-6 text-sm"><div><div className="text-muted-foreground text-xs uppercase tracking-widest">Call</div><div className="font-medium mt-1">9428609248</div></div><div><div className="text-muted-foreground text-xs uppercase tracking-widest">Email</div><div className="font-medium mt-1">booking@oitsinc.in</div></div></div></div>
        <form onSubmit={submitInquiry} className="border border-border rounded-xl p-6 bg-card grain relative" data-testid="inquiry-form"><div className="grid grid-cols-2 gap-4"><Input required placeholder="Your name" value={inq.name} onChange={(e) => setInq({ ...inq, name: e.target.value })} data-testid="inq-name" /><Input required type="tel" placeholder="Phone" value={inq.phone} onChange={(e) => setInq({ ...inq, phone: e.target.value })} data-testid="inq-phone" /></div><Input required type="email" placeholder="Email" className="mt-4" value={inq.email} onChange={(e) => setInq({ ...inq, email: e.target.value })} data-testid="inq-email" /><Textarea required placeholder="Where would you like to go? Any dates in mind?" rows={5} className="mt-4" value={inq.message} onChange={(e) => setInq({ ...inq, message: e.target.value })} data-testid="inq-message" /><Button type="submit" disabled={sending} className="mt-5 rounded-full bg-accent text-accent-foreground hover:bg-accent/90 w-full" data-testid="inq-submit">{sending ? "Sending..." : "Send inquiry"}</Button></form>
      </div></section>
      <section id="about" className="max-w-4xl mx-auto px-6 py-24 text-center"><div className="text-xs uppercase tracking-widest text-accent mb-3">About OITS</div><h2 className="heading-serif text-4xl sm:text-5xl mb-6">Travel Together, remember forever.</h2><p className="text-muted-foreground leading-relaxed">OITS is founded and led by Soham Dave, a travel professional with over 25 years of experience in the travel industry. His expertise and passion for travel shape every journey we create.</p><p className="text-muted-foreground leading-relaxed mt-4">Our promise is simple — explore the world without compromising on what matters to you. We take special care to provide pure vegetarian and Jain food anywhere in the world, making every journey more comfortable, thoughtful and worry-free.</p><p className="text-muted-foreground leading-relaxed mt-4">Travel together. Create memories. Remember forever.</p></section>
      <section id="office" className="max-w-7xl mx-auto px-6 pb-24">
        <div className="mb-8 text-center">
          <div className="text-xs uppercase tracking-widest text-accent mb-3">Visit Us</div>
          <h2 className="heading-serif text-4xl sm:text-5xl mb-4">Find Our Office</h2>
          <p className="mx-auto flex max-w-2xl items-start justify-center gap-2 text-muted-foreground leading-relaxed">
            <MapPin className="mt-1 h-5 w-5 shrink-0 text-accent" />
            <span>116/117, SP Nirvana, opposite Zaveri Green, near VIP Road, Bopal, Ahmedabad, Gujarat – 380058</span>
          </p>
          <a
            className="mt-4 inline-flex text-sm font-medium text-accent underline-offset-4 hover:underline"
            href="https://www.google.com/maps/place/Outbound+International+Travel+Services/@23.0248167,72.4485786,17z/data=!3m1!4b1!4m6!3m5!1s0xc3b2439e1d1a3c5:0x93d22722ca0f9622!8m2!3d23.0248167!4d72.4511535!16s%2Fg%2F11nqz43hfq"
            target="_blank"
            rel="noreferrer"
          >
            Get directions <ArrowRight className="ml-2 h-4 w-4" />
          </a>
        </div>
        <div className="overflow-hidden rounded-2xl border border-border">
        <div className="relative">
          <iframe
            title="OITS - Outbound International Travel Services office location"
            src="https://maps.google.com/maps?q=23.0248167,72.4511535&z=17&output=embed"
            width="100%"
            height="420"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />

          {/* OITS label overlay */}
          <div className="absolute left-1/2 top-[48%] -translate-x-1/2 -translate-y-full pointer-events-none">
            <div className="rounded-md bg-white px-3 py-1 text-sm font-bold text-black shadow-md border border-gray-200">
              OITS
            </div>
          <div className="mx-auto h-0 w-0 border-l-[6px] border-r-[6px] border-t-[7px] border-l-transparent border-r-transparent border-t-white" />
        </div>
      </div>
    </div>
      </section>
    </div>
  );
}
